// Miniaturki klockow do katalogu (palety).
//
// Rysujemy je TYMI SAMYMI funkcjami co edytor i symulacje (drawPart/drawWheel),
// wiec miniaturka wyglada dokladnie jak klocek na plotnie - gracz uczy sie
// rozpoznawac czesci, zamiast czytac teksty o wymaganych kolach i osiach.
//
// Obrys liczymy z RZECZYWISTYCH ksztaltow czesci (partBoundsGrid), wiec kolo
// ogromne dostaje tyle miejsca, ile naprawde zajmuje, a nie kwadrat 1x1.

import { partBoundsGrid } from '../domain/geometry';
import type { PartDefinition, PartInstance } from '../domain/types';
import { METERS_PER_GRID } from '../physics/units';
import { Camera } from './camera';
import { drawPart, drawWheel, type PartStyle } from './partArt';

/** Zapas wokol miniaturki (wspolczynnik) - klocek nie dotyka krawedzi. */
const THUMB_PADDING = 1.3;

/** Klocek w neutralnej pozycji - do policzenia obrysu i narysowania. */
function thumbInstance(definition: PartDefinition): PartInstance {
  return { id: 'thumb', typeId: definition.typeId, gridX: 0, gridY: 0, rotation: 0 };
}

/** Kamera dopasowana do obrysu jednego klocka, w polu `size` x `size` pikseli. */
export function thumbnailCamera(definition: PartDefinition, size: number): Camera {
  const bounds = partBoundsGrid(thumbInstance(definition), definition);
  const minX = bounds.minX * METERS_PER_GRID;
  const maxX = bounds.maxX * METERS_PER_GRID;
  const minY = bounds.minY * METERS_PER_GRID;
  const maxY = bounds.maxY * METERS_PER_GRID;

  const spanX = Math.max(1e-3, (maxX - minX) * THUMB_PADDING);
  const spanY = Math.max(1e-3, (maxY - minY) * THUMB_PADDING);

  const camera = new Camera();
  camera.resize(size, size);
  camera.scale = Math.min(size / spanX, size / spanY);
  camera.centerX = (minX + maxX) / 2;
  camera.centerY = (minY + maxY) / 2;
  return camera;
}

/**
 * Rysuje miniaturek jednego klocka w kwadracie `size` x `size` pikseli.
 * Kola ida osobna sciezka (bieznik i szprychy), bryly - wspolna funkcja czesci.
 */
export function drawPartThumbnail(
  ctx: CanvasRenderingContext2D,
  definition: PartDefinition,
  size: number,
  style: PartStyle = {},
): void {
  const camera = thumbnailCamera(definition, size);

  if (definition.wheel) {
    const radiusMeters = (definition.wheel.radiusGrid ?? 0.5) * METERS_PER_GRID;
    drawWheel(
      ctx,
      camera,
      {
        definition,
        centerX: 0.5 * METERS_PER_GRID,
        centerY: 0.5 * METERS_PER_GRID,
        angle: 0,
        radiusMeters,
      },
      style,
    );
    return;
  }

  drawPart(ctx, camera, { definition, originX: 0, originY: 0, angle: 0 }, style);
}
